import { Component, computed, DestroyRef, effect, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NzPopconfirmDirective } from 'ng-zorro-antd/popconfirm';
import { DeleteReferralComponent } from '../delete-referral/delete-referral.component';
import { SendReminderModalComponent } from '../send-reminder-modal/send-reminder-modal.component';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { InvitationsService } from 'core';
import { ReferralModalFacade } from '../../../shared/services/referral-modal.facade';
import {
  GoBackComponent,
  IEvent,
  IStep,
  KorviuButtonComponent,
  KorviuLoaderComponent,
  StepsV2Component,
  TimelineV2Component,
  UserAvatarV2Component
} from 'shared';
import { User, UserTypes } from 'shared/types/user.model';
import { IReminder } from 'shared/types/invitation-reminders.interface';
import { TagComponent } from 'shared/components/tag/tag.component';
import { IReferral } from 'core/types/referral.interface';
import { BreadcrumbComponent } from '../../../blocks/breadcrumb/breadcrumb.component';
import { BreadcrumbService } from '../../../blocks/breadcrumb/breadcrumb.service';
import { AuthService } from 'core/services/auth.service';
import { catchError, map, of, switchMap, tap, throwError } from 'rxjs';
import {
  buildWhatsappSharePayload,
  ensureWhatsappShareLogged,
  getWhatsappMessageFromUrl
} from 'shared/utils/whatsapp-share.utils';
import { CustomMessageService } from 'core/services/custom-message.service';
import { resolveReferralPhoneParts } from 'shared/utils/referral-contact.utils';
import {
  buildReferralSignupDeepLink,
  replaceReferralTemplatePlaceholders
} from 'shared/utils/referral-template-placeholders.utils';
import { HideTooltipOnScrollDirective } from 'shared/directives';
import { IInvitation } from 'core/types/invitation.interface';
import { IInvitationReminders } from 'shared/types/invitation-reminders.interface';
import { InvitationContactType, MAX_INVITATION_REMINDERS } from 'core/types/invitation-contact.type';
import { ResourceLoadErrorComponent } from 'shared/components/resource-load-error/resource-load-error.component';

@Component({
  selector: 'app-my-referral-info',
  imports: [
    KorviuLoaderComponent,
    UserAvatarV2Component,
    TranslatePipe,
    NzPopconfirmDirective,
    HideTooltipOnScrollDirective,
    NzButtonComponent,
    NzIconModule,
    StepsV2Component,
    TimelineV2Component,
    KorviuButtonComponent,
    TagComponent,
    GoBackComponent,
    BreadcrumbComponent,
    ResourceLoadErrorComponent
  ],
  templateUrl: './my-referral-info.component.html',
  styleUrl: './my-referral-info.component.scss'
})
export class MyReferralInfoComponent {
  #activatedRoute = inject(ActivatedRoute);
  #modalService = inject(NzModalService);
  #invitationsService = inject(InvitationsService);
  #destroyRef = inject(DestroyRef);
  #referralModalFacade = inject(ReferralModalFacade);
  #breadcrumbService = inject(BreadcrumbService);
  #authService = inject(AuthService);
  #messageService = inject(CustomMessageService);
  #translateService = inject(TranslateService);

  contactMethod = computed<InvitationContactType | null>(() =>
    this._normalizeContactMethodType(
      this.invite()?.invitation_type?.name || this.invite()?.type || this.invite()?.contact_method
    )
  );

  contactMethodLocaleKey = computed<string>(() => {
    const method = this.contactMethod();

    if (method === 'email') {
      return 'my-referrals.email';
    }

    if (method === 'whatsapp') {
      return 'referral.whatsApp';
    }

    return '';
  });

  emailContact = computed<string>(() => {
    const invite = this.invite();
    return invite?.email?.trim() || (this.contactMethod() === 'email' ? invite?.value?.trim() || '' : '');
  });

  phoneContact = computed<string>(() => {
    const invite = this.invite();
    const rawPhoneNumber = invite?.phone_number?.trim() || '';
    if (!rawPhoneNumber) {
      return '';
    }

    const hasCountryCode = !!invite?.country_code?.trim();
    const hasKnownInternationalPrefix =
      rawPhoneNumber.replace(/\D/g, '').length > 10 && /^\+?(?:52|1)/.test(rawPhoneNumber);
    if (!hasCountryCode && !hasKnownInternationalPrefix) {
      return rawPhoneNumber;
    }

    const { countryCode, phoneNumber } = resolveReferralPhoneParts({
      countryCode: invite?.country_code,
      phoneNumber: rawPhoneNumber,
      localPhoneNumber: invite?.local_phone_number,
      liveInUs: invite?.live_in_us
    });

    return countryCode && phoneNumber ? `+${countryCode} ${phoneNumber}` : rawPhoneNumber;
  });

  isGanasocio = computed<boolean>(() => this.referral()?.invited_user_type?.code === UserTypes.PR);
  readonly referralStatusLocaleKey = computed(() => (this.referral()?.status === 'Seed' ? 'my-referrals.seed' : null));
  steps = computed<IStep[]>(() => {
    const timeline = this.timeline();

    if (this.isGanasocio()) {
      return [
        {
          iconSrc: 'assets/img/my-forest/steps/Seed.svg',
          label: 'my-referrals.seed',
          date: timeline?.timeline?.Seed || ''
        },
        {
          iconSrc: 'assets/img/my-forest/steps/Signed.svg',
          label: 'my-referrals.signedContract',
          date: timeline?.timeline?.SignedContract || ''
        },
        {
          iconSrc: 'assets/img/my-forest/steps/ganasocio-2.svg',
          label: 'my-referrals.ganasocio',
          date: timeline?.timeline?.Ganasocio || ''
        }
      ];
    }

    return [
      {
        iconSrc: 'assets/img/my-forest/steps/Seed.svg',
        label: 'my-referrals.seed',
        date: timeline?.timeline?.Seed || ''
      },
      {
        iconSrc: 'assets/img/my-forest/steps/Ganacuate.svg',
        label: 'my-referrals.ganacuate',
        date: timeline?.timeline?.Ganacuate || ''
      },
      {
        iconSrc: 'assets/img/my-forest/steps/Ganarbol.svg',
        label: 'my-referrals.ganarbol',
        date: timeline?.timeline?.Ganarbol || ''
      },
      {
        iconSrc: 'assets/img/my-forest/steps/Winprizes.svg',
        label: 'my-referrals.winPrizes',
        date: timeline?.timeline?.Winprizes || ''
      }
    ];
  });

  timelineEvents = computed<IEvent[]>(() => {
    const inviteData = this.invite();
    const remindersData = this.reminders();
    const timeline = this.timeline();

    const events: IEvent[] = [];

    // Initial invitation
    if (inviteData) {
      events.push({
        date: timeline?.timeline?.Seed || '',
        title: { locale: 'referral.initialInvitation' },
        description: {
          locale: 'referral.sentReferralMessageViaWhatsApp',
          value: {
            method: this._getContactMethodLabel(
              inviteData.original_invitation_type ||
                inviteData.invitation_type?.name ||
                inviteData.type ||
                inviteData.contact_method
            )
          }
        }
      });
    }

    // Reminders - always show all 3, even if not sent
    const reminderTitles: Record<number, string> = {
      1: 'referral.firstReminder',
      2: 'referral.secondReminder',
      3: 'referral.thirdReminder'
    };

    const remindersMap = new Map<number, IReminder>();
    if (remindersData?.reminders) {
      remindersData.reminders.forEach((reminder: IReminder) => {
        remindersMap.set(reminder.intent, reminder);
      });
    }

    // Always add all 3 reminders
    for (let intent = 1; intent <= 3; intent++) {
      const reminder = remindersMap.get(intent);
      if (reminder && reminder.timestamp) {
        events.push({
          date: reminder.timestamp,
          title: { locale: reminderTitles[intent] },
          description: {
            locale: 'referral.sent',
            value: {
              status: this._getContactMethodLabel(
                reminder.contact_type ||
                  inviteData?.invitation_type?.name ||
                  inviteData?.type ||
                  inviteData?.contact_method
              )
            }
          },
          suggestion: reminder.message ? this._replacePlaceholders(reminder.message, inviteData) : undefined
        });
      } else {
        events.push({
          date: '',
          title: { locale: reminderTitles[intent] },
          description: { locale: 'referral.notSent' }
        });
      }
    }

    return events;
  });

  params = toSignal(this.#activatedRoute.params, { initialValue: {} });

  inviteId = computed(() => +this.params()?.['id']);

  referralAsUserResource = this.#invitationsService.getReferralByIdAsUserResource(this.inviteId());
  referralResource = this.#invitationsService.getReferralByIdResource(this.inviteId());
  invitationResource = this.#invitationsService.getInvitationByIdResource(this.inviteId());
  timelineResource = this.#invitationsService.getTimelineByInvitationIdResource(this.inviteId());
  remindersResource = this.#invitationsService.getRemindersResource(this.inviteId());

  user = computed<User | null>(() =>
    this.referralAsUserResource.hasValue() ? this.referralAsUserResource.value() : null
  );
  invite = computed<IInvitation | null>(() =>
    this.invitationResource.hasValue() ? this.invitationResource.value() : null
  );
  timeline = computed(() => (this.timelineResource.hasValue() ? this.timelineResource.value() : null));
  reminders = computed<IInvitationReminders | null>(() =>
    this.remindersResource.hasValue() ? this.remindersResource.value() : null
  );
  referral = computed(() => (this.referralResource.hasValue() ? this.referralResource.value() : null));

  remindersCount = computed<number>(() => {
    const remindersData = this.reminders();
    return remindersData?.total_reminders ?? remindersData?.reminders?.length ?? 0;
  });

  reminderButtonLocale = computed<string>(() => {
    const remindersCount = this.remindersCount();

    if (remindersCount === 0) {
      return 'my-referrals.sendFirstReminder';
    } else if (remindersCount === 1) {
      return 'my-referrals.sendSecondReminder';
    } else {
      return 'my-referrals.sendLastReminder';
    }
  });

  canSendReminder = computed<boolean>(() => {
    return this.remindersResource.hasValue() && !!this.invite() && this.remindersCount() < MAX_INVITATION_REMINDERS;
  });

  showItsBeenWhile = computed<boolean>(() => {
    const remindersData = this.reminders();
    const timeline = this.timeline();
    const remindersCount = this.remindersCount();

    // Don't show if all reminders are already sent
    if (remindersCount >= MAX_INVITATION_REMINDERS) {
      return false;
    }

    // Get the last contact date (last reminder or initial invitation)
    let lastContactDate: Date | null = null;

    if (remindersCount > 0) {
      const lastReminder = remindersData?.reminders?.at(-1);
      if (lastReminder?.timestamp) {
        lastContactDate = new Date(lastReminder.timestamp);
      }
    } else if (timeline?.timeline?.Seed) {
      lastContactDate = new Date(timeline.timeline.Seed);
    }

    if (!lastContactDate) {
      return false;
    }

    // Show tag if 7 or more days have passed since last contact
    const now = new Date();
    const daysSinceLastContact = Math.floor((now.getTime() - lastContactDate.getTime()) / (1000 * 60 * 60 * 24));
    return daysSinceLastContact >= 7;
  });

  allRemindersSent = computed<boolean>(() => {
    return this.remindersCount() >= MAX_INVITATION_REMINDERS;
  });

  isPendingApproval = computed<boolean>(() => {
    const invite = this.invite();
    return !!(invite?.approval_required || invite?.need_approval || invite?.approved === false);
  });

  canShareWhatsapp = computed<boolean>(() => {
    const invite = this.invite();
    const hasWhatsappContact =
      this.contactMethod() === 'whatsapp' || !!invite?.phone_number?.trim() || !!invite?.local_phone_number?.trim();

    return hasWhatsappContact && !this.isPendingApproval();
  });

  invitationAsUser = computed(() => {
    const invite = this.invite();
    return invite
      ? new User({
          ...invite,
          status: invite.status?.name
        })
      : null;
  });

  isLoading = computed(() => this.referralAsUserResource.isLoading());
  inviteIsLoading = computed(() => this.invitationResource.isLoading());
  referralIsLoading = computed(() => this.referralResource.isLoading());
  timelineIsLoading = computed(() => this.timelineResource.isLoading());
  remindersIsLoading = computed(() => this.remindersResource.isLoading());

  constructor() {
    this.#referralModalFacade.referralDetailReloadSubject.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
      this.invitationResource.reload();
      this.referralResource.reload();
      this.timelineResource.reload();
      this.remindersResource.reload();
    });

    effect(() => {
      const referral = this.referral();
      if (referral?.full_name) {
        this.#breadcrumbService.setCurrentPageBreadcrumb(referral.full_name, 'replacer=referralName');
      }
    });
  }

  onEditInvitationDetails(): void {
    const referral = this.invite();
    if (!referral) {
      return;
    }
    const phoneParts = resolveReferralPhoneParts({
      value: referral?.value,
      countryCode: referral?.country_code,
      phoneNumber: referral?.phone_number,
      localPhoneNumber: referral?.local_phone_number,
      liveInUs: referral?.live_in_us
    });
    const modalData = {
      userType: referral.invited_user_type?.code,
      isUpdate: true,
      prefillData: {
        id: referral?.id,
        contact_name: referral?.first_name,
        first_name: referral?.first_name,
        last_name: referral?.last_name,
        phone_number: phoneParts.phoneNumber,
        country_code: phoneParts.phoneNumber ? phoneParts.countryCode : null,
        org_name: referral?.org_name,
        email: referral?.email,
        website_link: referral?.website_link,
        good_partner: referral?.good_partner,
        live_in_us: referral?.live_in_us,
        language: typeof referral.language === 'string' ? referral.language : referral.language?.code,
        type: referral?.invitation_type?.name
      } as IReferral
    };
    this.#referralModalFacade.openAddNewReferralFormModal(modalData);
  }

  onDeleteAccountClick(): void {
    if (!this.referral()?.id) {
      return;
    }
    const referralId = this.referral()?.id;
    const name = this.referral()?.first_name + ' ' + this.referral()?.last_name;
    this.#modalService.create({
      nzContent: DeleteReferralComponent,
      nzWidth: 554,
      nzData: { referralId, name },
      nzCentered: true,
      nzWrapClassName: 'modal-wrap'
    });
  }

  sendReminder(): void {
    const invite = this.invite();

    if (!invite || !this.canSendReminder()) {
      return;
    }

    const referralInfo: IReferral = {
      ...invite,
      language: typeof invite.language === 'string' ? invite.language : invite.language?.code,
      reminders: this.reminders()?.reminders || invite.reminders || [],
      total_reminders: this.remindersCount()
    };

    const modal = this.#modalService.create({
      nzContent: SendReminderModalComponent,
      nzWidth: 554,
      nzData: { referralInfo },
      nzCentered: true,
      nzWrapClassName: 'modal-wrap'
    });
    modal.componentInstance.reminderSent.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
      this.invitationResource.reload();
      this.remindersResource.reload();
    });
  }

  openWhatsAppDesktop(): void {
    const invitationId = this.invite()?.id || this.inviteId();

    this.#invitationsService
      .getWhatsappLink(invitationId)
      .pipe(
        switchMap(linkData => {
          const whatsappUrl = linkData?.whatsapp_url;

          if (!whatsappUrl) {
            this._showWhatsappLinkError();
            return of(null);
          }

          window.open(whatsappUrl, '_blank');

          const message = linkData.message || getWhatsappMessageFromUrl(whatsappUrl);
          if (!message) {
            return throwError(() => new Error('WhatsApp share message is missing'));
          }

          return this.#invitationsService
            .logWhatsappShare(invitationId, buildWhatsappSharePayload(invitationId, message))
            .pipe(map(ensureWhatsappShareLogged));
        }),
        tap(() => {
          this.invitationResource.reload();
          this.remindersResource.reload();
        }),
        catchError(() => {
          this._showWhatsappLinkError();
          return of(null);
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  private _getCountryCode(phoneNumber: string): string | null {
    if (!phoneNumber) {
      return null;
    }

    return resolveReferralPhoneParts({ value: phoneNumber }).countryCode;
  }

  private _getNumberAfterCountryCode(phoneNumber: string): string | null {
    if (!phoneNumber) {
      return null;
    }

    return resolveReferralPhoneParts({ value: phoneNumber }).phoneNumber;
  }

  formatPhoneNumber(phoneNumber: string): string {
    if (!phoneNumber) {
      return '-';
    }

    const countryCode = this._getCountryCode(phoneNumber);
    const number = this._getNumberAfterCountryCode(phoneNumber);

    if (!countryCode || !number) {
      return phoneNumber;
    }

    if (countryCode === '52') {
      // Формат Мексики: +52 (XX) XXXX XXXX
      const formatted = number.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2 $3');
      return `+52 ${formatted}`;
    }

    if (countryCode === '1') {
      // Формат США: +1 (XXX) XXX-XXXX
      const formatted = number.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
      return `+1 ${formatted}`;
    }

    return phoneNumber;
  }

  private _replacePlaceholders(template: string, inviteData: any): string {
    const currentUser = this.#authService.currentUser;
    const referralCode = currentUser?.referralCode;

    return replaceReferralTemplatePlaceholders(template, {
      referralFirstName: inviteData?.first_name,
      referringMemberName: currentUser?.firstName,
      referralCode,
      signupDeepLink: buildReferralSignupDeepLink(window.location.origin, referralCode)
    });
  }

  private _normalizeContactMethodType(type?: string): InvitationContactType | null {
    const normalizedType = type?.toLowerCase();

    if (normalizedType === 'email') {
      return 'email';
    }

    if (normalizedType === 'whatsapp') {
      return 'whatsapp';
    }

    if (normalizedType === 'sms' || normalizedType === 'phone') {
      return 'whatsapp';
    }

    return null;
  }

  private _getContactMethodLabel(type?: string): string {
    const contactMethod = this._normalizeContactMethodType(type);

    if (contactMethod === 'email') {
      return 'Email';
    }

    if (contactMethod === 'whatsapp') {
      return 'WhatsApp';
    }

    return type || '-';
  }

  private _showWhatsappLinkError(): void {
    this.#messageService.error(this.#translateService.instant('referral.whatsappLinkUnavailable'));
  }
}
